import { Helmet } from 'react-helmet';
import { useContext, useMemo } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useGlobalQueries } from '@/hooks/use-global-queries';
import { AppContext } from '@/contexts/app-context';
import { getImageUrl } from '@/utils/images';
import { decodeHtmlEntities, removeEnclosingSlashes } from '@/utils/strings';

export const MetaTags = () => {
    const context = useContext(AppContext);

    const { pathname } = useLocation();
    const [searchParams] = useSearchParams();
    const { pageQuery, getSetting } = useGlobalQueries();

    if (!pageQuery.data) {
        return null;
    }

    const is404 = useMemo(() => pageQuery.data?.post_title === '404', [pageQuery.data]);
    const pageTitle = useMemo(() => decodeHtmlEntities(pageQuery.data?.page_title || pageQuery.data?.post_title) || 'Resurs Bank', [pageQuery.data]);
    const ogImage = useMemo(() => getImageUrl(pageQuery.data?.og_image?.src || pageQuery.data?.featured_image?.src), [pageQuery.data]);

    const getCanonicalUrl = (defaultPathname: string = '') => {
        const humanySettings = getSetting('humany_settings');
        const url = pageQuery.data?.canonical_url || defaultPathname;
        const normalizedPathname = `/${removeEnclosingSlashes(pathname)}`;
        const humanyPage = humanySettings?.humany_page_link ? `/${removeEnclosingSlashes(humanySettings?.humany_page_link)}` : null;
        const humanyBusinessPage = humanySettings?.humany_page_link_business
            ? `/${removeEnclosingSlashes(humanySettings?.humany_page_link_business)}`
            : null;

        if (humanyPage && normalizedPathname.startsWith(humanyPage)) {
            return `${context.url}${normalizedPathname}`;
        }

        if (humanyBusinessPage && normalizedPathname.startsWith(humanyBusinessPage)) {
            return `${context.url}${normalizedPathname}`;
        }

        if (pathname.startsWith('/resurs-insights')) {
            const page = searchParams.get('page');
            const group = searchParams.get('group');
            const params = new URLSearchParams();

            if (page) {
                params.append('page', page);
            }
            if (group) {
                params.append('group', group);
            }

            const insightsUrl = `${context.url}/${removeEnclosingSlashes(pathname)}`;
            return params.size ? `${insightsUrl}?${params.toString()}` : insightsUrl;
        }

        return url ? `${context.url}/${removeEnclosingSlashes(url)}` : `${context.url}/${removeEnclosingSlashes(pathname)}`;
    };

    return (
        <Helmet>
            <title>{pageTitle}</title>
            {!is404 ? <link rel="canonical" href={getCanonicalUrl()} /> : null}
            {!is404 ? <link rel="alternate" hrefLang={context.language} href={getCanonicalUrl(pathname)} /> : null}

            {pageQuery.data?.meta_title ? <meta name="title" content={pageQuery.data?.meta_title} /> : null}
            {pageQuery.data?.meta_description ? <meta name="description" content={pageQuery.data?.meta_description} /> : null}

            <meta property="og:type" content="website" />
            {!is404 ? <meta property="og:url" content={getCanonicalUrl()} /> : null}

            {pageQuery.data?.og_title || pageQuery.data?.page_title ? (
                <meta property="og:title" content={pageQuery.data?.og_title || pageQuery.data?.page_title} />
            ) : null}
            {!is404 && (pageQuery.data?.og_description || pageQuery.data?.meta_description) ? (
                <meta property="og:description" content={pageQuery.data?.og_description || pageQuery.data?.meta_description} />
            ) : null}
            {ogImage ? <meta property="og:image" content={ogImage} /> : null}
        </Helmet>
    );
};
